// Header.tsx
import { format } from 'date-fns'
import {
  Box,
  Flex,
  HStack,
  Button,
  Image,
  Text,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  IconButton,
  VStack,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Divider,
  Icon
} from '@chakra-ui/react'
import BigNumber from 'bignumber.js'
import { HamburgerIcon } from '@chakra-ui/icons'
import { useNavigate } from 'react-router-dom'
import { useRef, useMemo } from 'react'
import { authState } from '../../atoms/auth'
import { useRecoilState } from 'recoil'
import { signOut } from '../../services/apiService'
import { useAuthState } from '../../utils/authUtils'
import {
  FiUser,
  FiLogOut,
  FiSend,
  FiUsers,
  FiSettings,
  FiGlobe
} from 'react-icons/fi'
import { useTranslation } from 'react-i18next'

const Header = () => {
  const navigate = useNavigate()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const [auth] = useRecoilState(authState)
  const toast = useToast()
  const { initUser } = useAuthState()
  const { t, i18n } = useTranslation()

  // 언어 목록
  const languages = useMemo(
    () => [
      { code: 'ko', label: t('한국어'), flag: '/images/flags/ko.png' },
      { code: 'en', label: t('영어'), flag: '/images/flags/en.png' },
      { code: 'ja', label: t('일본어'), flag: '/images/flags/ja.png' },
      { code: 'th', label: t('태국어'), flag: '/images/flags/th.png' },
      { code: 'vi', label: t('베트남어'), flag: '/images/flags/vi.png' },
      { code: 'zh', label: t('중국어'), flag: '/images/flags/zh.png' }
    ],
    [t]
  )

  const currentLang = i18n.language?.split('-')[0] || 'ko'
  const currentFlag =
    languages.find((l) => l.code === currentLang)?.flag ??
    '/images/flags/ko.png'

  const changeLanguage = async (lng: string) => {
    await i18n.changeLanguage(lng)
    try {
      localStorage.setItem('i18nextLng', lng)
    } catch {}
    toast({
      title: t('언어가 변경되었습니다.'),
      description: t('현재 언어') + `: ${lng.toUpperCase()}`,
      status: 'success',
      duration: 1500,
      isClosable: true
    })
  }

  const isValidFuture = (iso?: string | null) => {
    if (!iso) return false
    const t = Date.parse(iso)
    if (Number.isNaN(t)) return false
    return t > Date.now()
  }

  const getGradeLabel = (auth: any) => {
    if (isValidFuture(auth?.subscriptionAt)) return 'SECRET'
    if (isValidFuture(auth?.referralAt)) return 'VIP'
    if (isValidFuture(auth?.serviceAt)) return 'BASIC'
    return 'NORMAL'
  }

  const getGradeColor = (auth: any) => {
    if (isValidFuture(auth?.subscriptionAt)) return 'cyan.500'
    if (isValidFuture(auth?.referralAt)) return 'yellow.500'
    if (isValidFuture(auth?.serviceAt)) return 'blue.500'
    return 'gray.500'
  }

  const handleClickLogout = async () => {
    const { data: res } = await signOut()
    if (res.statusCode === 200) {
      initUser()
      toast({
        title: t('로그아웃'),
        description: res.message,
        status: 'success',
        duration: 3000,
        isClosable: true
      })
      navigate('/')
    }
  }

  return (
    <Box
      bg="dark.bg"
      w="100%"
      borderBottom="1px"
      borderColor="dark.border"
      px={4}
      py={2}
    >
      <Flex
        align="center"
        maxW="7xl"
        mx="auto"
        justify="space-between"
        height={'57px'}
      >
        {/* 왼쪽: 햄버거 + 로고 */}
        <Flex align="center">
          <IconButton
            ref={btnRef}
            icon={<HamburgerIcon />}
            aria-label={t('메뉴 열기')}
            variant="ghost"
            display={{ base: 'inline-flex', md: 'none' }}
            onClick={onOpen}
            mr={2}
          />
          <Flex align="center" cursor="pointer" onClick={() => navigate('/')}>
            <Text
              fontSize="xl"
              fontWeight="bold"
              bgGradient="linear(to-r, brand.500, brand.300)"
              bgClip="text"
            >
              WaveBot
            </Text>
          </Flex>
        </Flex>

        {/* 오른쪽: 데스크탑 메뉴 + 로그인 */}
        <Flex align="center" gap={4}>
          <HStack
            spacing={4}
            align="center"
            display={{ base: 'none', md: 'flex' }}
          >
            {/* <Text
              _hover={{ color: 'brand.500', cursor: 'pointer' }}
              onClick={() => navigate('/spot')}
            >
              {t('현물')}
            </Text> */}
            <Text
              _hover={{ color: 'brand.500', cursor: 'pointer' }}
              onClick={() => navigate('/trading')}
            >
              {t('선물')}
            </Text>
            {/* <Text
              _hover={{ color: 'brand.500', cursor: 'pointer' }}
              onClick={() => navigate('/strategy')}
            >
              {t('전략')}
            </Text> */}
            {/* <Text
              _hover={{ color: 'brand.500', cursor: 'pointer' }}
              onClick={() => navigate('/realtime')}
            >
              {t('실시간')}
            </Text> */}
            {/* <Text
              _hover={{ color: 'black', cursor: 'pointer' }}
              onClick={() => navigate('/ranking')}
            >
              {t('랭킹')}
            </Text> */}
            <Text
              _hover={{ color: 'brand.500', cursor: 'pointer' }}
              onClick={() => navigate('/news')}
            >
              {t('뉴스')}
            </Text>
            <Text
              _hover={{ color: 'brand.500', cursor: 'pointer' }}
              onClick={() => navigate('/community')}
            >
              {t('커뮤니티')}
            </Text>
            <Text
              _hover={{ color: 'brand.500', cursor: 'pointer' }}
              onClick={() => navigate('/store')}
            >
              {t('스토어')}
            </Text>
            <Text
              _hover={{ color: 'brand.500', cursor: 'pointer' }}
              onClick={() => navigate('/pointshop')}
            >
              {t('포인트샵')}
            </Text>
            <Text
              _hover={{ color: 'brand.500', cursor: 'pointer' }}
              onClick={() => navigate('/certificate')}
            >
              {t('인증서')}
            </Text>
          </HStack>

          {/* 언어 선택 (데스크톱) */}
          <Menu placement="bottom-end">
            <MenuButton
              as={Button}
              size="sm"
              variant="outline"
              color="gray.200"
              borderColor="gray.600"
              _hover={{ borderColor: 'brand.500', color: 'brand.400' }}
              leftIcon={<FiGlobe />}
              rightIcon={
                <Image
                  src={currentFlag}
                  alt={currentLang}
                  boxSize="18px"
                  borderRadius="full"
                />
              }
            >
              {t('언어')}
            </MenuButton>
            <MenuList zIndex={1400}>
              {languages.map((lang) => (
                <MenuItem
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                >
                  <HStack spacing={3}>
                    <Image
                      src={lang.flag}
                      alt={lang.label}
                      boxSize="18px"
                      borderRadius="full"
                    />
                    <Text>{lang.label}</Text>
                  </HStack>
                </MenuItem>
              ))}
            </MenuList>
          </Menu>

          {auth.id ? (
            <>
              <Menu>
                <MenuButton>
                  <Avatar
                    size="sm"
                    name={auth.username}
                    src={auth.profileImageUrl || undefined}
                    cursor="pointer"
                  />
                </MenuButton>
                <MenuList zIndex={1400}>
                  <Box px={4} py={3}>
                    <VStack spacing={3} align="flex-start">
                      <HStack>
                        <Text fontSize="sm" color="gray.300">
                          {t('아이디')}
                        </Text>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="white"
                          isTruncated
                          maxW="140px"
                        >
                          {auth.account}
                        </Text>
                      </HStack>

                      <HStack>
                        <Text fontSize="sm" color="gray.300">
                          {t('나의등급')}
                        </Text>
                        <Text
                          fontSize="sm"
                          fontWeight="bold"
                          color={getGradeColor(auth)}
                          isTruncated
                          maxW="140px"
                        >
                          {getGradeLabel(auth)}
                        </Text>
                      </HStack>

                      <HStack>
                        <Text fontSize="sm" color="gray.300">
                          {t('이용일자')}
                        </Text>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="white"
                          isTruncated
                          maxW="140px"
                        >
                          {auth.serviceAt
                            ? format(
                                new Date(auth.serviceAt),
                                'yyyy-MM-dd HH:mm'
                              )
                            : t('구독중이지 않습니다.')}
                        </Text>
                      </HStack>

                      <HStack>
                        <Text fontSize="sm" color="gray.300">
                          {t('테더')}
                        </Text>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="white"
                          isTruncated
                          maxW="140px"
                        >
                          {new BigNumber(auth.usdtBalance).toFormat(0)} USDT
                        </Text>
                      </HStack>

                      <HStack>
                        <Text fontSize="sm" color="gray.300">
                          {t('포인트')}
                        </Text>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="brand.500"
                          isTruncated
                          maxW="140px"
                        >
                          {new BigNumber(auth.pointBalance).toFormat(0)} P
                        </Text>
                      </HStack>

                      <HStack>
                        <Text fontSize="sm" color="gray.300">
                          {t('닉네임')}
                        </Text>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color={auth.nickname ? "green.600" : "red.500"}
                          isTruncated
                          maxW="140px"
                        >
                          {auth.nickname || t('설정 안됨')}
                        </Text>
                      </HStack>
                    </VStack>
                  </Box>

                  <Divider my={3} />

                  <MenuItem onClick={() => navigate('/mypage')}>
                    <HStack spacing={3} w="full">
                      <FiUser />
                      <Text>{t('마이페이지')}</Text>
                    </HStack>
                  </MenuItem>

                  <MenuItem onClick={() => navigate('/withdrawal')}>
                    <HStack spacing={3} w="full">
                      <FiSend />
                      <Text>{t('출금')}</Text>
                    </HStack>
                  </MenuItem>

                  <MenuItem onClick={() => navigate('/partner')}>
                    <HStack spacing={3} w="full">
                      <FiUsers />
                      <Text>{t('파트너')}</Text>
                    </HStack>
                  </MenuItem>

                  {auth.isAdmin === 1 && (
                    <MenuItem onClick={() => navigate('/admin/user')}>
                      <HStack spacing={3} w="full">
                        <FiSettings />
                        <Text>{t('관리자')}</Text>
                      </HStack>
                    </MenuItem>
                  )}

                  <MenuItem onClick={handleClickLogout}>
                    <HStack spacing={3} w="full">
                      <FiLogOut />
                      <Text>{t('로그아웃')}</Text>
                    </HStack>
                  </MenuItem>
                </MenuList>
              </Menu>
            </>
          ) : (
            <>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={() => navigate('/signIn')}
              >
                {t('로그인')}
              </Button>
            </>
          )}
        </Flex>
      </Flex>

      {/* 모바일 드로어 메뉴 */}
      <Drawer
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        finalFocusRef={btnRef}
      >
        <DrawerOverlay />
        <DrawerContent bg="dark.card">
          <DrawerCloseButton />
          <DrawerHeader>
            <Flex
              align="center"
              cursor="pointer"
              onClick={() => {
                navigate('/')
                onClose()
              }}
            >

              <Text
                fontSize="lg"
                fontWeight="bold"
                bgGradient="linear(to-r, brand.500, brand.300)"
                bgClip="text"
              >
                WaveBot
              </Text>
            </Flex>
          </DrawerHeader>

          <DrawerBody>
            {/* 언어 선택 (모바일 드로어 상단) */}
            <VStack align="flex-start" spacing={3} mb={4}>
              <Text fontWeight="bold">{t('언어')}</Text>
              <HStack wrap="wrap" spacing={3}>
                {languages.map((lang) => (
                  <Button
                    key={lang.code}
                    size="sm"
                    variant={currentLang === lang.code ? 'solid' : 'outline'}
                    leftIcon={
                      <Image
                        src={lang.flag}
                        alt={lang.label}
                        boxSize="16px"
                        borderRadius="full"
                      />
                    }
                    onClick={() => changeLanguage(lang.code)}
                  >
                    {lang.label}
                  </Button>
                ))}
              </HStack>
            </VStack>

            <VStack
              align="flex-start"
              spacing={4}
              fontSize="md"
              color="gray.300"
            >
              {/* <Text
                onClick={() => {
                  navigate('/spot')
                  onClose()
                }}
                cursor="pointer"
              >
                {t('현물')}
              </Text> */}
              <Text
                onClick={() => {
                  navigate('/trading')
                  onClose()
                }}
                cursor="pointer"
              >
                {t('선물')}
              </Text>
              {/* <Text
                onClick={() => {
                  navigate('/strategy')
                  onClose()
                }}
                cursor="pointer"
              >
                {t('전략')}
              </Text> */}
              {/* <Text
                onClick={() => {
                  navigate('/realtime')
                  onClose()
                }}
                cursor="pointer"
              >
                {t('실시간')}
              </Text> */}
              {/* <Text
                onClick={() => {
                  navigate('/ranking')
                  onClose()
                }}
                cursor="pointer"
              >
                {t('랭킹')}
              </Text> */}
              <Text
                onClick={() => {
                  navigate('/news')
                  onClose()
                }}
                cursor="pointer"
              >
                {t('뉴스')}
              </Text>
              <Text
                onClick={() => {
                  navigate('/community')
                  onClose()
                }}
                cursor="pointer"
              >
                {t('커뮤니티')}
              </Text>
              <Text
                onClick={() => {
                  navigate('/store')
                  onClose()
                }}
                cursor="pointer"
              >
                {t('스토어')}
              </Text>
              <Text
                onClick={() => {
                  navigate('/pointshop')
                  onClose()
                }}
                cursor="pointer"
              >
                {t('포인트샵')}
              </Text>
              <Text
                onClick={() => {
                  navigate('/certificate')
                  onClose()
                }}
                cursor="pointer"
              >
                {t('인증서')}
              </Text>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  )
}

export default Header
